import { Suspense, useEffect, useMemo, useState } from "react"

// ** Third Party Components
import axios from "axios"
import { he } from "date-fns/locale"
import { parseCookies } from "nookies"
import { formatDistance } from "date-fns"
import { MessageCircleWarning } from "lucide-react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate, useSearchParams } from "react-router-dom"

// ** Internal Element
import Loader from "@components/common/loader"
import PmsLoader from "@components/common/loader"
import { Button } from "@components/common/button"
import { Separator } from "@components/common/separator"
import { ScrollArea } from "@components/common/scroll-area"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@components/common/sheet"

// ** Types
import { DictType } from "@pma/types"

// ** Store
import { useDrawer } from "@stores/drawer-store"
import { useAuth } from "@pma/stores/auth-store"

// ** API Routes
import { USERS_PAGE_ROUTE } from "@pma/utils/page-routes"
import { NOTES_BY_TASK_ID_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"

// ** Constants
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Helpers
import { cn, formatTime } from "@pma/utils/helpers"

// ** Custom Hooks
import useTranslator from "@hooks/use-translator"

// ** Custom Components
import NotesThread from "./notes-thread"
import NoteTextArea from "./note-text-area"
import { TaskActivity } from "../activities/task-activity"
import AttachFileToTask from "../projects/task-attached-files"

const NotesDrawer = () => {
  const drawer = useDrawer()
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const queryClient = useQueryClient()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()

  const taskId = drawer?.taskId
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const newParams = new URLSearchParams(searchParam)
  const revalidateKey = searchParam.get("revalidate")
  const showBtn = newParams.toString()?.includes("&showBtn=")
  const organizationUsers = authConfig?.organizationUsers || []

  const [userList, setUserList] = useState([])
  const [activeTab, setActiveTab] = useState("notes")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const getTaskNotes = async () => {
    if (!taskId) return

    const response = await axios.get(SERVER_URL + NOTES_BY_TASK_ID_ENDPOINT + taskId, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    })
    return response.data
  }

  const {
    data: taskNotesData,
    isLoading: isLoadingNotes,
    refetch: refetchNotes,
  } = useQuery({
    queryKey: ["task-notes", taskId],
    queryFn: getTaskNotes,
    enabled: !!taskId,
    staleTime: 0,
  })

  const taskData = taskNotesData?.task

  const toggleInbox = () => {
    if (drawer.isOpen && drawer?.context === "notes") {
      drawer.onClose()
      let found = newParams.toString()

      if (found.includes("&showBtn=")) {
        found = newParams.toString().split("&showBtn=")[0]
      }

      return navigate(`?${found}`)
    } else {
      drawer.onOpen()
      drawer.setContext("notes")
      return
    }
  }

  // Fixed: includes taskNotesData in deps, uses spread to avoid mutating original
  const sortedNotes = useMemo(
    () =>
      [...(taskNotesData?.notes || [])].sort(
        (a: DictType, b: DictType) => b.created_at - a.created_at,
      ),
    [taskNotesData],
  )

  // Fixed: refetch first, then navigate after fetch completes.
  // Also uses queryClient.invalidateQueries to ensure cache is busted.
  useEffect(() => {
    if (revalidateKey === "tasks" && taskId) {
      const run = async () => {
        await queryClient.invalidateQueries({ queryKey: ["task-notes", taskId] })
        await refetchNotes()

        const params = new URLSearchParams(searchParam)
        params.delete("revalidate")
        navigate(`?${params.toString()}`, { replace: true })
      }
      run()
    }
  }, [revalidateKey, taskId])

  const isHebrew = locale === "he"
  const sheetSide = isHebrew ? "left" : "right"
  const isOpen = drawer.context === "notes" && drawer.isOpen

  useEffect(() => {
    if (taskData) {
      const newList = organizationUsers.filter((user: any) =>
        taskData.responsible_id.includes(user.id),
      )
      setUserList(newList)
    }
  }, [taskData])

  if (taskId === 0) {
    return (
      <Suspense fallback={<Loader />}>
        <Sheet open={isOpen} onOpenChange={toggleInbox}>
          <SheetContent className='w-full px-4 md:w-[1080px]' side={sheetSide}>
            <div className='flex h-[90vh] w-full flex-col items-center justify-center gap-2 py-12'>
              <MessageCircleWarning size={30} />
              <div className='flex flex-col text-center'>
                <p className='text-xl'>{translate("Unable to find task")}</p>
                <p className='text-sm text-slate-300'>{translate("Task may have been deleted")}.</p>
              </div>
              <Button
                variant={"link"}
                className='p-0 text-xs'
                onClick={() => {
                  if (drawer.fromUpdates) drawer.setContext("updates")
                  else drawer.setContext("notifications")
                }}
              >
                {drawer.fromUpdates
                  ? translate("Back to updates")
                  : translate("Back to notifications")}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </Suspense>
    )
  }

  return (
    <Suspense fallback={<Loader />}>
      <Sheet open={isOpen} onOpenChange={toggleInbox}>
        <SheetContent className='w-full px-4 md:w-[1080px]' side={sheetSide}>
          {showBtn && (
            <Button
              variant={"link"}
              className='p-0 text-xs'
              onClick={() => {
                if (drawer.fromUpdates) drawer.setContext("updates")
                else drawer.setContext("notifications")
              }}
            >
              {drawer.fromUpdates
                ? translate("Back to updates")
                : translate("Back to notifications")}
            </Button>
          )}
          <SheetHeader>
            <SheetTitle className={isHebrew ? "text-right" : "text-left"}>
              {translate(taskData?.title)}
            </SheetTitle>
          </SheetHeader>
          <SheetDescription className={cn("w-full", isHebrew ? "rtl" : "ltr")}>
            <div
              className={cn("flex w-full justify-between", isHebrew ? "text-right" : "text-left")}
            >
              <div className='flex flex-col'>
                <p className={cn("text-slate-500", isHebrew ? "text-right" : "text-left")}>
                  {taskData?.description}
                </p>
                <small className={cn("text-[11px]", isHebrew ? "text-right" : "text-left")}>
                  {taskData?.created_at &&
                    formatDistance(taskData?.created_at, new Date(), {
                      locale: isHebrew ? he : undefined,
                      addSuffix: true,
                    })}{" "}
                  @{" "}
                  {formatTime(taskData?.created_at, {
                    amPm: true,
                    locale: isHebrew ? "he" : undefined,
                  })}{" "}
                </small>{" "}
              </div>
              <div className='flex items-center'>
                {userList?.map((user: DictType, index: number) => (
                  <Tooltip key={index}>
                    <TooltipTrigger asChild>
                      <Link
                        to={USERS_PAGE_ROUTE + `/${user?.id}`}
                        className='-ml-2.5 flex h-7 w-7 items-center justify-center rounded-full border p-0'
                      >
                        <img
                          alt=''
                          src={
                            user?.user_picture?.url
                              ? user.user_picture.url
                              : `${process.env.PUBLIC_URL}/ionaddoutline@2x.png`
                          }
                          className='h-7 w-7 rounded-full'
                        />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>{user?.name}</TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </div>
          </SheetDescription>
          {isLoadingNotes ? (
            <PmsLoader />
          ) : (
            <div className='my-4'>
              <div className={cn("flex gap-6 text-[12px]", isHebrew ? "rtl" : "ltr")}>
                <Button
                  onClick={() => setActiveTab("notes")}
                  variant={"ghost"}
                  className={cn(
                    "m-0 h-fit min-w-[80px] rounded-none p-0 text-[12px] hover:bg-transparent",
                    activeTab === "notes" && "border-b-2 border-primary",
                  )}
                >
                  {translate("Notes")}
                </Button>
                <Button
                  onClick={() => setActiveTab("files")}
                  variant={"ghost"}
                  className={cn(
                    "m-0 h-fit min-w-[80px] rounded-none p-0 text-[12px] hover:bg-transparent",
                    activeTab === "files" && "border-b-2 border-primary",
                  )}
                >
                  {translate("Files")}
                </Button>
                <Button
                  onClick={() => setActiveTab("activities")}
                  variant={"ghost"}
                  className={cn(
                    "m-0 h-fit min-w-[80px] rounded-none p-0 text-[12px] hover:bg-transparent",
                    activeTab === "activities" && "border-b-2 border-primary",
                  )}
                >
                  {translate("Activities")}
                </Button>
              </div>
              <Separator />
              <ScrollArea
                className={cn("h-[73vh] min-h-fit pe-3 pt-2", isHebrew ? "rtl" : "ltr")}
                scrollHideDelay={0}
              >
                {activeTab === "notes" && (
                  <>
                    <NoteTextArea
                      task={taskData}
                      setUpdateRecord={drawer.setUpdateRecord}
                      setIsSubmitting={setIsSubmitting}
                    />
                    {(sortedNotes?.length > 0 || isSubmitting) && (
                      <NotesThread notes={sortedNotes} isSubmitting={isSubmitting} />
                    )}
                  </>
                )}
                {activeTab === "activities" && <TaskActivity task={taskData} />}

                {activeTab === "files" && (
                  <AttachFileToTask
                    taskId={taskData?.id}
                    size='lg'
                    files={taskData?.linked_files_id}
                    showAsFolder={false}
                  />
                )}
              </ScrollArea>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </Suspense>
  )
}

export default NotesDrawer
